/* mk-film.js — plays the brand films rendered by snippets/mk-film.liquid, and
   lazy-loads the posters/photos of the sections that use them.

   A film costs nothing until it is on screen: the MP4 URL waits in
   <source data-src> and is only attached the first time the video is needed.
   After that it plays while visible and pauses the moment it is not, so a rail
   of seventeen films never decodes more than the few in view.

   Three modes, from data-mk-film:
   - view   plays whenever it is on screen (the film rail, SUNFORGED chapters)
   - hover  plays while its [data-mk-film-host] is hovered or focused, on a
            real pointer; on touch it plays only while the section marks it
            .is-active (audience cards: the card nearest the middle of the screen)
   - manual only plays when a section calls mkFilm.play(); this script still
            pauses it off screen and resumes it on return if the section
            marked it with .is-active (the scent-family stage)

   Deliberately NOT done:
   - no autoplay under prefers-reduced-motion or Save-Data; the poster stays,
     and it is a real frame of the film, so the card still reads as intended.
     The same goes for a very slow connection (see ADAPTIVE FILMS below): each
     film picks 1080p, 720p, 480p or no film at all from the connection and the
     device, and steps down by itself when it stalls.
   - no sound, no controls: these are atmosphere, the product page carries the
     full film with controls in the gallery.

   Horizon scrolls .page-wrapper rather than the window on desktop. An
   IntersectionObserver with the default root measures against the viewport
   through any scroll container, so it works there without special casing.

   LAZY IMAGES (speed pass, 25 Sep 2026). <img data-mk-src="..." data-mk-w="290,435,580">
   gets its src (and a srcset built from data-mk-w by swapping the width= parameter)
   only once it is within one screen height of the screen (NEAR). Chrome's native
   loading="lazy" starts 1250-2500 px early, which on the homepage meant every poster,
   stage still and audience visual (~1.3 MB) competed with the hero image. While it
   waits, the img carries a 1x1 transparent GIF as src: an img without any src draws a
   broken-image icon, a hairline and its alt text, where the old lazy img showed nothing
   yet. Options:
   - data-mk-lazy="view": only once it is actually on screen (the hidden stage stills).
   - an ancestor [data-mk-rail] (a horizontal scroller): an image that comes near also
     loads the images of the previous item and of the next rail-width of items (at least
     two), so a swipe or an arrow click never lands on an empty card. Items outside the
     scroller's box are clipped, so they do not count as near until they are swiped
     towards. An image loaded early as a neighbour stays observed, so when it comes into
     view it pulls in the next ones.
   The near-observer uses .page-wrapper as root when that is the scroller (desktop): a
   rootMargin on the viewport does not reach inside a nested scroll container.
   The look-ahead starts at the window load event or at the first scroll / touch /
   key press, whichever comes first. Until then only images that are actually on screen
   load, so the film rail just below the fold (~330px on a phone) no longer takes
   bandwidth from the hero image while the page is still loading. */
(function () {
  'use strict';

  // Several sections include this file; a second <script> with the same src
  // still executes, so the first one wins and the rest just re-bind.
  if (window.mkFilm) { window.mkFilm.bind(); return; }

  var still =
    matchMedia('(prefers-reduced-motion: reduce)').matches ||
    !!(navigator.connection && navigator.connection.saveData);
  var finePointer = matchMedia('(hover: hover) and (pointer: fine)');
  var hasIO = 'IntersectionObserver' in window;

  /* ---------- lazy images ---------- */

  // One screen height ahead (of .page-wrapper's height where that is the root).
  var NEAR = '100% 0px 100% 0px';

  function img(el) {
    var s = el && el.getAttribute('data-mk-src');
    if (!s) return;
    el.removeAttribute('data-mk-src');
    var w = el.getAttribute('data-mk-w');
    if (w) {
      el.srcset = w.split(',').map(function (x) {
        return s.replace(/([?&]width=)\d+/, '$1' + x) + ' ' + x + 'w';
      }).join(', ');
    }
    el.src = s;
  }

  function hit(el) {
    img(el);
    var rail = el.closest('[data-mk-rail]');
    if (!rail) return;
    var item = el;
    while (item && item.parentElement !== rail) item = item.parentElement;
    if (!item) return;
    // Look ahead by one "page" of the rail (as many items as fit side by side, at least
    // two): a phone swipe moves one card, the desktop arrows move two of five.
    var w = item.getBoundingClientRect().width;
    var ahead = Math.max(2, w ? Math.round(rail.clientWidth / w) : 2);
    var kids = rail.children, k = [].indexOf.call(kids, item);
    for (var j = k - 1; j <= k + ahead; j++) {
      if (j < 0 || j >= kids.length || j === k) continue;
      [].forEach.call(kids[j].querySelectorAll('img[data-mk-src]:not([data-mk-lazy])'), img);
    }
  }

  function isScroller(el) {
    var o = getComputedStyle(el).overflowY;
    return o === 'auto' || o === 'scroll';
  }

  var pw = document.querySelector('.page-wrapper');
  function onEntries(root) {
    return function (entries) {
      // A root that does not scroll (the page wrapper on phones) holds the whole page,
      // so everything "intersects" it; only the viewport observer counts then.
      if (root && !isScroller(root)) return;
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        forget(e.target);
        hit(e.target);
      });
    };
  }
  function forget(el) {
    nearObs.forEach(function (o) { o.unobserve(el); });
    if (viewObs) viewObs.unobserve(el);
  }
  var nearObs = [], viewObs = null, nearOn = false;
  if (hasIO) viewObs = new IntersectionObserver(onEntries(null), { rootMargin: '0px' });

  function near(el) { return el.getAttribute('data-mk-lazy') !== 'view'; }

  function armNear() {
    if (nearOn || !hasIO) return;
    nearOn = true;
    GATE.forEach(function (t) { removeEventListener(t, armNear, true); });
    if (pw) nearObs.push(new IntersectionObserver(onEntries(pw), { root: pw, rootMargin: NEAR }));
    nearObs.push(new IntersectionObserver(onEntries(null), { rootMargin: NEAR }));
    document.querySelectorAll('img[data-mk-src]').forEach(function (el) {
      if (el._mkLazy && near(el)) nearObs.forEach(function (o) { o.observe(el); });
    });
  }
  var GATE = ['scroll', 'touchstart', 'pointerdown', 'keydown', 'wheel'];
  if (hasIO) {
    if (document.readyState === 'complete') armNear();
    else {
      addEventListener('load', armNear);
      GATE.forEach(function (t) { addEventListener(t, armNear, { capture: true, passive: true }); });
    }
  }

  function lazy(root) {
    (root || document).querySelectorAll('img[data-mk-src]').forEach(function (el) {
      if (el._mkLazy) return;
      el._mkLazy = true;
      if (!hasIO) { img(el); return; }
      viewObs.observe(el);
      if (nearOn && near(el)) nearObs.forEach(function (o) { o.observe(el); });
    });
  }

  /* ---------- adaptive quality ----------

     ADAPTIVE FILMS (26 Sep 2026). Every film is a Shopify video with three MP4
     renditions next to each other; only the name differs (HD-1080p / HD-720p /
     SD-480p, each with its own bitrate). Liquid lists just the 1080p file, so the
     snippet derives 720p for small surfaces, and this script picks per film, at
     the moment it is first needed:

       tier 0  1080p   only where the snippet already asked for it (a big surface,
                       target > 450: the family stage, SUNFORGED), on a fast link
       tier 1  720p    the default for the rail and the audience cards
       tier 2  480p    slow links, weak devices, or several films loading at once
       tier 3  still   no film at all: the poster / card image stays

     The estimate is made once per page (net()):
     - navigator.connection (Chrome, Android): effectiveType, downlink, rtt.
       Chrome caps downlink at 10 Mbps, so 10 means "10 or more" (taken as 30).
     - the Resource Timing entries of the page itself (the only source in Safari,
       iOS and Firefox; in Chrome the larger of the two is used). Entries over
       30 KB that came over the network (transferSize > 0) give
       bytes / (responseEnd - responseStart); the median of those, and the bytes of
       ALL network entries over the union of their download windows, are both lower
       bounds of the link, so the larger one is used. Fewer than 3 entries over
       30 KB: no estimate (720p as before). Measured at 9 Mbps / 150 ms: 6.2 Mbps,
       so a phone without navigator.connection lands on 480p or 720p there, and
       on 720p on a normal wifi (217 Mbps measured unthrottled).
     - device: navigator.deviceMemory (Chromium only). 1 GB: at most 480p; 2 GB: no
       1080p. hardwareConcurrency only counts together with deviceMemory, because
       Safari reports a clamped value that says nothing about the phone.
     Thresholds:
     - still: effectiveType slow-2g or 2g, or an estimate under 0.8 Mbps. The 480p
       files need 0.8-1.5 Mbps, so below that a film only ever buffers.
     - at most 480p: effectiveType 3g.
     - otherwise the highest tier (never above the snippet's) whose real bitrate fits
       in 80% of the link divided by the number of films loading at the same moment
       (the films on screen that have not finished downloading, this one included).
       On a phone that is one film; the desktop rail starts four or five at once.
       The films are long (9-66 s; one 720p file is 1.5-24 MB), so most of them are
       still downloading while they play; one that is fully buffered stops counting.

     At run time a film steps DOWN one tier, keeping its position, when:
     - it has not shown a frame 4 s after play() (START_MS) and the link was not
       measured as fast (FAST, 8 Mbps), or
     - it waits for data for more than 1.5 s while playing (STALL_MS), or
     - it stalls twice (waits of 300 ms or more; three times at 480p), or
     - more than 30% of its frames are dropped over 90 frames (weak decoder); 480p
       is not stepped down for this.
     Films armed after that start no higher than the tier stepped down to. A film
     that steps below 480p is switched off: its <source> is removed, so nothing keeps
     downloading, and the poster underneath stays. A film that was blocked (play()
     rejected, low-power mode) is paused, not stalled, and is left alone.
     While a film reloads at a lower tier it goes back to opacity 0 (data-mk-fade),
     so the poster shows instead of an empty frame, and fades in again on `playing`.

     Not used: the HLS (.m3u8) source. Safari would adapt by itself, but Shopify's
     master playlist lists 1080p first, so every film would start at 1080p and only
     then step down; for a ten-second loop that ramp is most of its life. Chrome on
     desktop cannot play it at all. */

  // Shopify picks one bitrate ladder per upload (Mbps in the file name, 1080/720/480).
  // Seen on all homepage films, 26 Sep 2026. A film on an unknown ladder keeps its
  // file (it can still be switched off, just not stepped down). Add new ladders here.
  var RUNG = ['HD-1080p', 'HD-720p', 'SD-480p'];
  var LADDERS = [
    ['7.2', '4.5', '1.5'],
    ['4.8', '3.0', '1.2'],
    ['3.3', '2.1', '1.0'],
    ['2.5', '1.6', '0.9']
  ];
  var TOKEN = /(HD-1080p|HD-720p|SD-480p)-([\d.]+)Mbps/;
  // The names match the files' average bitrate (measured on all 19 homepage films,
  // e.g. Nautilus 4.99 / 3.14 / 1.26 Mbps); 10% on top covers the peaks.
  var REAL = 1.1;
  var STILL = 3;
  var START_MS = 4000, STALL_MS = 1500, FAST = 8;

  function ladder(url) {
    var m = TOKEN.exec(url || '');
    if (!m) return null;
    var r = RUNG.indexOf(m[1]);
    for (var i = 0; i < LADDERS.length; i++) {
      if (LADDERS[i][r] === m[2]) return { tok: m[0], rates: LADDERS[i], tier: r };
    }
    return null;
  }
  function urlAt(base, L, t) { return base.replace(L.tok, RUNG[t] + '-' + L.rates[t] + 'Mbps'); }
  function mbpsAt(L, t) { return parseFloat(L.rates[t]) * REAL; }

  function timing() {
    if (!window.performance || !performance.getEntriesByType) return null;
    var rates = [], spans = [], bytes = 0;
    performance.getEntriesByType('resource').forEach(function (r) {
      var d = r.responseEnd - r.responseStart;
      var size = r.transferSize;
      // Browsers without transferSize: encodedBodySize, but a cache hit then looks
      // like an absurdly fast download, so very short ones are skipped.
      if (size == null) { size = r.encodedBodySize; if (d < 8) return; }
      if (!(size > 0) || !(d > 0)) return;
      // Every download counts towards the total (small files share the link with
      // the big ones); only files over 30 KB give a rate of their own.
      spans.push([r.responseStart, r.responseEnd]);
      bytes += size;
      if (size > 30000) rates.push(size * 8 / d / 1000);
    });
    if (rates.length < 3) return null;
    rates.sort(function (a, b) { return a - b; });
    var med = rates[rates.length >> 1];
    spans.sort(function (a, b) { return a[0] - b[0]; });
    var busy = 0, s = spans[0][0], e = spans[0][1];
    for (var i = 1; i < spans.length; i++) {
      if (spans[i][0] > e) { busy += e - s; s = spans[i][0]; e = spans[i][1]; }
      else if (spans[i][1] > e) e = spans[i][1];
    }
    busy += e - s;
    var agg = busy > 0 ? bytes * 8 / busy / 1000 : 0;
    return Math.round(Math.max(med, agg) * 100) / 100;
  }

  var NET = null;
  function net() {
    if (NET) return NET;
    var c = navigator.connection || {};
    var n = NET = { mbps: null, from: '', type: c.effectiveType || '', rtt: c.rtt, cap: 0, floor: 0, still: false, log: [] };
    var rt = timing();
    if (typeof c.downlink === 'number' && c.downlink > 0) {
      n.mbps = c.downlink >= 10 ? 30 : c.downlink;
      n.from = 'connection';
      // Chrome's figure is coarse and can lag (9.4 on a fast line in a fresh
      // browser); this page's own downloads are a lower bound of the link as it is
      // now, so the larger of the two counts.
      if (rt != null && rt > n.mbps) { n.mbps = rt; n.from = 'timing'; }
    } else {
      n.mbps = rt;
      n.from = rt == null ? 'none' : 'timing';
    }
    var mem = navigator.deviceMemory, cores = navigator.hardwareConcurrency;
    if (mem && mem <= 1) n.cap = 2;
    else if (mem && (mem <= 2 || (cores && cores <= 2))) n.cap = 1;
    if (n.type === 'slow-2g' || n.type === '2g' || (n.mbps != null && n.mbps < 0.8)) n.still = true;
    else if (n.type === '3g') n.cap = 2;
    return n;
  }

  function wantsNow(v) { return v._mkSeen && !document.hidden && wants(v); }

  // Films that are downloading at the same moment as v (v included).
  function sharing(v) {
    var k = 1;
    document.querySelectorAll('video[data-mk-film]').forEach(function (o) {
      if (o !== v && !o._mkOff && !o._mkDone && (o._mkWant || wantsNow(o))) k++;
    });
    return k;
  }

  function choose(v, L) {
    var n = net();
    if (n.still) return STILL;
    if (!L) return -1;
    var t = Math.max(L.tier, n.cap, n.floor);
    if (n.mbps == null) return t;
    var budget = n.mbps * 0.8 / sharing(v);
    while (t < 2 && mbpsAt(L, t) > budget) t++;
    return t;
  }

  function mark(v, t) { v.setAttribute('data-mk-q', t >= STILL ? 'still' : t < 0 ? 'fixed' : RUNG[t]); }

  // Loads tier t into an armed film. From a running film it keeps the position.
  function setTier(v, t) {
    var s = v._mkS;
    unguard(v);
    v._mkTier = t;
    v._mkDone = false;
    v._mkPlayed = false;
    v._mkStalls = 0;
    v._mkQ = null;
    mark(v, t);
    if (v.hasAttribute('data-mk-fade')) v.style.opacity = '0';
    if (t >= STILL) {
      v._mkOff = true;
      v._mkWant = false;
      if (!v.paused) v.pause();
      if (s && s.parentNode) s.parentNode.removeChild(s);
      v.load();
      return;
    }
    var at = v.currentTime || 0;
    s.src = urlAt(v._mkBase, v._mkL, t);
    v.load();
    if (at > 0.2) {
      v.addEventListener('loadedmetadata', function () {
        try { v.currentTime = v.duration ? at % v.duration : at; } catch (e) { /* keeps 0 */ }
      }, { once: true });
    }
  }

  function down(v, why) {
    if (v._mkOff || v._mkTier == null) return;
    var n = net();
    var t = v._mkL ? v._mkTier + 1 : STILL;
    if (v._mkL && t > n.floor && t < STILL) n.floor = t;
    if (n.log.length < 40) {
      n.log.push({ at: Math.round(performance.now()), film: (v._mkBase || '').split('/').pop().slice(0, 8), to: t >= STILL ? 'still' : RUNG[t], why: why });
    }
    var resume = v._mkWant;
    setTier(v, t);
    if (resume && t < STILL) play(v);
  }

  function unguard(v) { if (v._mkT) { clearTimeout(v._mkT); v._mkT = 0; } }
  function guard(v, ms, why) {
    if (v._mkT || v._mkOff) return;
    v._mkT = setTimeout(function () {
      v._mkT = 0;
      // paused: blocked, scrolled away or hidden; that is not the connection's fault.
      if (v._mkOff || v.paused || document.hidden || v.readyState >= 3) return;
      // A film that never starts on a link measured as fast is not waiting for
      // bandwidth (seen in Chrome with many films open, live theme too); it keeps its
      // poster, as before, instead of pulling every later film down with it.
      if (why === 'start' && net().mbps >= FAST) return;
      down(v, why);
    }, ms);
  }

  function done(v) {
    var b = v.buffered;
    if (b && b.length && v.duration && b.end(b.length - 1) >= v.duration - 0.25) v._mkDone = true;
  }

  function frames(v) {
    if (!v.getVideoPlaybackQuality || v._mkOff || !v._mkL || !(v._mkTier < 2) || document.hidden) return;
    var q = v.getVideoPlaybackQuality();
    var b = v._mkQ;
    if (!b || q.totalVideoFrames < b.t) { v._mkQ = { t: q.totalVideoFrames, d: q.droppedVideoFrames }; return; }
    var n = q.totalVideoFrames - b.t;
    if (n < 90) return;
    var r = (q.droppedVideoFrames - b.d) / n;
    v._mkQ = { t: q.totalVideoFrames, d: q.droppedVideoFrames };
    if (r > 0.3) {
      var N = net();
      if (v._mkTier + 1 > N.cap) N.cap = v._mkTier + 1;
      down(v, 'frames');
    }
  }

  function watch(v) {
    var since = 0;
    v.addEventListener('playing', function () {
      unguard(v);
      if (since && v._mkPlayed && performance.now() - since >= 300) {
        v._mkStalls = (v._mkStalls || 0) + 1;
        // Two stalls step a film down; at 480p, where the next step is no film, three.
        if (v._mkStalls >= (v._mkTier >= 2 || !v._mkL ? 3 : 2)) { since = 0; down(v, 'stalls'); return; }
      }
      since = 0;
      v._mkPlayed = true;
      done(v);
    });
    v.addEventListener('waiting', function () {
      if (v._mkOff || v.paused) return;
      if (v._mkPlayed) { since = performance.now(); guard(v, STALL_MS, 'stall'); }
      else guard(v, START_MS, 'start');
    });
    v.addEventListener('pause', function () { unguard(v); since = 0; });
    v.addEventListener('progress', function () { done(v); });
    v.addEventListener('timeupdate', function () { frames(v); });
  }

  /* ---------- films ---------- */

  function arm(v) {
    if (v.dataset.mkArmed) return;
    v.dataset.mkArmed = '1';
    var s = v.querySelector('source[data-src]');
    if (!s) return;
    var src = s.getAttribute('data-src');
    // The 720p file is derived from the 1080p name (see the snippet); if a derived
    // file is ever missing, drop back to the file Liquid actually listed. data-fb is
    // the 1080p token of that name; data-fallback (a full URL) is still honoured.
    var tok = s.getAttribute('data-fb');
    var canon = s.getAttribute('data-fallback') || (tok ? src.replace(TOKEN, tok) : src);
    var L = ladder(src);
    v._mkS = s;
    v._mkBase = src;
    v._mkL = L;
    s.removeAttribute('data-src');
    // A stepped-down file that fails goes back to the snippet's own pick first, then
    // to the listed file; either way the film stops changing tiers after that.
    s.addEventListener('error', function () {
      var cur = s.getAttribute('src');
      if (v._mkOff || !canon || cur === canon) return;
      var next = cur !== src && src !== canon ? src : canon;
      if (next === src) { v._mkTier = L ? L.tier : -1; mark(v, v._mkTier); }
      else { v._mkTier = -1; mark(v, -1); }
      v._mkL = null;
      s.src = next;
      v.load();
      if (v._mkWant) play(v);
    });
    watch(v);
    var t = choose(v, L);
    if (t >= STILL) { setTier(v, STILL); return; }
    v._mkTier = t;
    mark(v, t);
    s.src = t >= 0 && L ? urlAt(src, L, t) : src;
    v.load();
  }

  function play(v) {
    if (still || !v || v._mkOff) return;
    arm(v);
    if (v._mkOff) return;
    v._mkWant = true;
    // Only when it has no frame to show yet; a film that resumes from memory is
    // playing before this could fire.
    if (v.readyState < 3) guard(v, START_MS, 'start');
    var p = v.play();
    if (p && p.catch) p.catch(function () { /* blocked; the poster stays */ });
  }

  function pause(v) {
    if (!v) return;
    v._mkWant = false;
    unguard(v);
    if (!v.paused) v.pause();
  }

  function wants(v) {
    var m = v.dataset.mkFilm;
    if (m === 'manual') return v.classList.contains('is-active');
    // hover films: on a mouse only via pointerenter (bound below); on touch the section
    // marks ONE of them .is-active (the card nearest the middle of the screen), so a
    // column of cards never plays three films at once.
    if (m === 'hover') return !finePointer.matches && v.classList.contains('is-active');
    return true;
  }

  // How much of a film must be on screen before it plays. Higher on touch: in the
  // homepage rail the next card peeks in by about half, and on a phone that would
  // mean two films decoding at once for one you are actually looking at.
  var need = finePointer.matches ? 0.35 : 0.6;

  // Two passes: every film in this batch is marked seen before the first one picks
  // its tier, so the desktop rail knows it is starting several films at once.
  var io = hasIO ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      e.target._mkSeen = e.isIntersecting && e.intersectionRatio >= need;
    });
    entries.forEach(function (e) {
      var v = e.target;
      if (v._mkSeen && !document.hidden && wants(v)) play(v);
      else if (!v._mkSeen) pause(v);
    });
  }, { threshold: [0, 0.35, 0.6, 0.8] }) : null;

  function bind(root) {
    lazy(root);
    if (still || !io) return;
    (root || document).querySelectorAll('video[data-mk-film]').forEach(function (v) {
      if (v._mkBound) return;
      v._mkBound = true;
      io.observe(v);
      if (v.hasAttribute('data-mk-fade')) {
        v.addEventListener('playing', function () { if (!v._mkOff) v.style.opacity = '1'; });
      }
      if (v.dataset.mkFilm === 'hover') {
        var host = v.closest('[data-mk-film-host]') || v.parentElement;
        var on = function () { if (finePointer.matches) play(v); };
        var off = function () { if (finePointer.matches) pause(v); };
        host.addEventListener('pointerenter', on);
        host.addEventListener('pointerleave', off);
        // A play() that is interrupted while the file is still loading (a stray
        // pointerleave, a layout shift) leaves the film paused although the mouse is
        // still on the card, and no new pointerenter will come. Measured on the Unisex
        // card, 25 Sep 2026. Any movement over the card restarts a stalled film.
        host.addEventListener('pointermove', function () { if (finePointer.matches && v.paused) play(v); });
        host.addEventListener('focusin', on);
        host.addEventListener('focusout', off);
      }
    });
  }

  document.addEventListener('visibilitychange', function () {
    document.querySelectorAll('video[data-mk-film]').forEach(function (v) {
      if (document.hidden) { unguard(v); if (!v.paused) v.pause(); }
      else if (v._mkSeen && wants(v)) play(v);
    });
  });

  // Theme editor: sections are re-rendered in place.
  document.addEventListener('shopify:section:load', function (e) { bind(e.target); });

  window.mkFilm = {
    still: still,
    bind: bind,
    // For manual films: plays only if the film is currently on screen, so a
    // section can call this freely without waking a film nobody can see.
    play: function (v) { if (v && (v._mkSeen || !io)) play(v); },
    pause: pause,
    // Loads a lazy image now (e.g. the still of the stage layer about to show).
    img: img,
    // Read-only: the connection estimate and every step down so far (debugging).
    net: function () { return net(); }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bind(); });
  } else {
    bind();
  }
})();
